import { useCallback, useMemo, useState } from 'react';

import {
  useLazyGetCustomListQuery,
  usePostCustomListMutation,
} from '../../../../features/ChatModule';
import { type EndpointBlock } from '../../../types';
import { DEFAULT_PAGINATION } from '../constants';
import { type EndpointParams } from '../types';

type QueryParams = Record<
  string,
  string | boolean | number | (string | boolean | number)[]
>;

const normalizeQueryParams = <T extends QueryParams>(
  qp: T
): {
  [K in keyof T]: T[K] extends (infer U)[] ? U : T[K];
} => {
  const entries = Object.entries(qp).map(([key, value]) => [
    key,
    Array.isArray(value) ? value[0] : value,
  ]);

  return Object.fromEntries(entries) as {
    [K in keyof T]: T[K] extends (infer U)[] ? U : T[K];
  };
};

const useFetchCustomList = ({
  url,
  method,
  payload,
  queryParams,
}: EndpointBlock) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  let fetchController;
  const getCustomListQuery = useLazyGetCustomListQuery();
  const postCustomListMutation = usePostCustomListMutation();

  const isGetMethod = useMemo(() => Boolean(method === 'GET'), [method]);

  if (isGetMethod) {
    fetchController = getCustomListQuery;
  } else {
    fetchController = postCustomListMutation;
  }

  const [fetchDependentFields] = fetchController;

  const handleFetchCustomList = useCallback(
    async ({
      max,
      offset,
      payload: runtimePayload,
      queryParams: runtimeQueryParams,
    }: EndpointParams = DEFAULT_PAGINATION): Promise<unknown> => {
      setIsLoading(true);
      setIsError(false);

      const normalizedQueryParams = normalizeQueryParams(
        (queryParams ?? {}) as QueryParams
      );
      const normalizedRuntimeQueryParams = normalizeQueryParams(
        (runtimeQueryParams ?? {}) as QueryParams
      );
      const dataQueryParams = {
        ...normalizedQueryParams,
        ...normalizedRuntimeQueryParams,
      };

      try {
        const response = await fetchDependentFields({
          url,
          data: isGetMethod
            ? {
                ...dataQueryParams,
                max,
                offset,
              }
            : { ...payload, ...runtimePayload, max, offset },
        }).unwrap();

        return response;
      } catch (error) {
        setIsError(true);

        throw Error(JSON.stringify(error));
      } finally {
        setIsLoading(false);
      }
    },
    [url, fetchDependentFields, isGetMethod, queryParams, payload]
  );

  return {
    handleFetchCustomList,
    isLoading,
    isError,
  };
};

// eslint-disable-next-line import/prefer-default-export
export { useFetchCustomList };
